import { styled } from '@mui/material';
import cx from 'classnames';
import { ComponentProps } from 'react';
import { GLOBAL_TAB_LABEL_CLASSES } from 'src/components/GlobalTabs/tab-label-classes';

type SpanProps = ComponentProps<'span'>;

// The label parts carry stable class names because the parent Tab, not the label, decides how
// they render: the selected state and the trailing mask are both applied from the Tab's styles.
const withLabelClass = (Component: React.ElementType, labelClass: string) => {
  const Styled = ({ className, ...props }: SpanProps) => (
    <Component className={cx(labelClass, className)} {...props} />
  );
  return Styled;
};

// Two rows: a small reference line over the identifying text. Sharing one line cost the title
// most of the tab's width, so the only way to widen it was to widen the tab. Stacking gives the
// title a whole row, which makes tabs narrower rather than wider. Both rows fit the height a
// single row already used, so the second row costs nothing vertically.
const ContentRoot = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textAlign: 'left',
});

// Row 1 — reference. Small and muted: it is what you scan by, not what you read.
const MainRoot = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: '0.625rem',
  // MuiTab's button typography sets 500 on everything inside the label. Both rows opt back out
  // so that the selected tab's 600 on the title is a real step up rather than a half one.
  fontWeight: 400,
  lineHeight: 1.1,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  minWidth: 0,
}));

// Truncates before the id does: the partner is the more expendable half of the reference row.
const OverlineRoot = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const IdRoot = styled('span')({
  fontVariantNumeric: 'tabular-nums',
  flexShrink: 0,
});

const SeparatorRoot = styled('span')({
  opacity: 0.5,
  flexShrink: 0,
});

// Row 2 — the identifying part, full width, truncating last.
const TitleRoot = styled('span')(({ theme }) => ({
  fontSize: '0.71875rem',
  fontWeight: 400,
  lineHeight: 1.25,
  color: theme.palette.custom.OV.sys.onBackground,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}));

export const TabLabelContent = withLabelClass(ContentRoot, GLOBAL_TAB_LABEL_CLASSES.content);
export const TabOverline = withLabelClass(OverlineRoot, GLOBAL_TAB_LABEL_CLASSES.overline);
export const TabMain = withLabelClass(MainRoot, GLOBAL_TAB_LABEL_CLASSES.main);
export const TabId = withLabelClass(IdRoot, GLOBAL_TAB_LABEL_CLASSES.id);
export const TabSeparator = withLabelClass(SeparatorRoot, GLOBAL_TAB_LABEL_CLASSES.separator);
export const TabTitle = withLabelClass(TitleRoot, GLOBAL_TAB_LABEL_CLASSES.title);

export const TabUnseenDot = styled('span')(({ theme }) => ({
  flexShrink: 0,
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.OV.sys.primary,
}));
